"use client";

import { SDUIOnClickHandler } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/SDUIOnClickHandler/SDUIOnClickHandler";
import { ActionLoggingEventParams } from "@faire/retailer-visitor-shared/components/SDUI/__internal__/types";
import { getSlateButtonVariant } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Button/getSlateButtonVariant";
import { useNumHiddenTiles } from "@faire/retailer-visitor-shared/components/SDUI/ComponentRenderers/Grid/__internal__/GridV2/useNumHiddenTiles";
import { Button } from "@faire/web--source/slate/Button";
import { Arrow } from "@faire/web--source/slate/icons/foundation/Arrow";
import { ResponsiveValues } from "@faire/web--source/slate/Theme/ResponsiveValues";
import { cn } from "@faire/web--source/tools/cn";
import { useResponsiveValue } from "@faire/web--source/ui/hooks/useResponsiveValue";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { IGridComponent } from "@faire/web-api--source/indigofair/data/IGridComponent";
import React from "react";

export const SDUIGridV2ExpandButton: React.FC<{
  isExpanded: boolean;
  truncationBehaviour: ResponsiveValues<IGridComponent.ITruncationBehaviour>;
  numTiles: number;
  containerRef: React.RefObject<HTMLDivElement | null>;
  tileRef: React.RefObject<HTMLDivElement | null>;
  columnSpacingPixels: number;
  rowSpacingPixels: number;
  onClick: () => void;
  loggingEventParams: ActionLoggingEventParams;
}> = ({
  isExpanded,
  truncationBehaviour,
  numTiles,
  containerRef,
  tileRef,
  columnSpacingPixels,
  onClick,
  loggingEventParams,
}) => {
  const deviceTruncationBehaviour = useResponsiveValue(truncationBehaviour);
  const numHiddenTiles = useNumHiddenTiles(
    containerRef,
    tileRef,
    columnSpacingPixels,
    numTiles
  );

  if (isExpanded || numHiddenTiles === 0 || !deviceTruncationBehaviour.expand) {
    return null;
  }

  const onClickActions = deviceTruncationBehaviour.expand?.on_click ?? [];
  const buttonVariant = getSlateButtonVariant(
    deviceTruncationBehaviour.expand?.variant
  );

  return (
    <SDUIOnClickHandler
      className={cn(
        "mx-auto mt-2 mb-0 w-max",
        !truncationBehaviour.mobileAndAbove?.expand || isExpanded
          ? "hidden"
          : "visible",
        !truncationBehaviour.tabletAndAbove?.expand || isExpanded
          ? "md:hidden"
          : "md:visible",
        !truncationBehaviour.desktopAndAbove?.expand || isExpanded
          ? "lg:hidden"
          : "lg:visible"
      )}
      onClickActions={onClickActions}
      loggingData={loggingEventParams}
    >
      <Button variant={buttonVariant} onClick={onClick}>
        {numHiddenTiles === undefined ? (
          <StrictLocalMsg
            defaultMessage="Show more"
            description={{
              text: "Show grid items that are more than certain number of rows that was previously made hidden",
              img: "https://cdn.faire.com/fastly/43888c804cb4a00e94c69bdb9293f0276fcb33143979e2580d4b8e4cc726b113.png",
            }}
          />
        ) : (
          <StrictLocalMsg
            defaultMessage="{tileNumber, plural, one {Show # more} other {Show # more}}"
            description={{
              text: "Show grid items that are more than certain number of rows that was previously made hidden",
              img: "https://cdn.faire.com/fastly/43888c804cb4a00e94c69bdb9293f0276fcb33143979e2580d4b8e4cc726b113.png",
            }}
            values={{
              tileNumber: numHiddenTiles,
            }}
          />
        )}
        <Arrow direction="down" width="16px" height="16px" />
      </Button>
    </SDUIOnClickHandler>
  );
};
